import { useState } from 'react'
import { toast } from 'sonner'
import { useCreateShipment } from '@/api/hooks'
import type { components } from '@/api/schema'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { formatApiErrorMessage } from '@/lib/api-error'
import { FREIGHT_MODE_LABEL, estimatedHandlingUnits, pickupDefaults, priorShipment } from './shipment-booking'
import type { PurchaseOrderDetail } from './types'

type CreateBody = components['schemas']['CreateShipmentRequest']
type Booker = 'us' | 'manufacturer'
type Mode = 'parcel' | 'ltl'

interface ItemDraft {
  id: number
  model: string
  ordered: number
  quantity: number
  included: boolean
}

function shippableItems(po: PurchaseOrderDetail): ItemDraft[] {
  return (po.line_items ?? [])
    .filter(
      (li) =>
        li.pricing_source !== 'manufacturer_dropship_fee' &&
        li.pricing_source !== 'manufacturer_tariff' &&
        li.line_kind !== 'fee' &&
        li.line_kind !== 'accessorial',
    )
    .map((li) => ({
      id: li.id,
      model: li.model || `Item ${li.id}`,
      ordered: li.quantity,
      quantity: li.quantity,
      included: true,
    }))
}

/** Boxes from the PO's last shipment, else the PO's package estimate. */
function defaultUnits(po: PurchaseOrderDetail): CreateBody['handling_units'] {
  const prior = priorShipment(po)?.handling_units ?? []
  if (prior.length === 0) return estimatedHandlingUnits(po)
  return prior.map((unit) => ({
    unit_type: unit.unit_type,
    num_units: unit.num_units,
    stackable: unit.stackable ?? false,
    length_in: unit.length_in,
    width_in: unit.width_in,
    height_in: unit.height_in,
    commodities: (unit.commodities ?? []).map((c) => ({
      description: c.description,
      weight_lbs: c.weight_lbs,
      pieces: c.pieces,
    })),
  }))
}

/**
 * Add a shipment in place (USA-1505): who books, how it ships and which items. A shipment we book
 * opens filled from the PO, its last shipment and the manufacturer, ready to check and book. A
 * supplier's shipment takes the carrier and tracking they gave us.
 */
export function AddShipmentPanel({ po, onDone }: { po: PurchaseOrderDetail; onDone: () => void }) {
  const create = useCreateShipment(po.uuid)
  const [booker, setBooker] = useState<Booker>(po.we_book_freight ? 'us' : 'manufacturer')
  const [mode, setMode] = useState<Mode | ''>(po.freight_mode === 'parcel' || po.freight_mode === 'ltl' ? po.freight_mode : '')
  const [items, setItems] = useState<ItemDraft[]>(() => shippableItems(po))
  const [choosingItems, setChoosingItems] = useState(false)
  const [carrier, setCarrier] = useState('')
  const [tracking, setTracking] = useState('')
  const [pro, setPro] = useState('')
  const [shipDate, setShipDate] = useState('')

  const included = items.filter((item) => item.included && item.quantity > 0)
  const wholePo = included.length === items.length && included.every((item) => item.quantity === item.ordered)
  const unitCount = included.reduce((sum, item) => sum + item.quantity, 0)

  const submit = () => {
    if (included.length === 0) {
      toast.error('Pick at least one item for this shipment')
      return
    }
    const lineItems = included.map((item) => ({ line_item_id: item.id, quantity: item.quantity }))
    const units = wholePo ? (defaultUnits(po) ?? []) : []
    // Total weight agrees with the boxes when we have them; the carrier checks the two match.
    const unitsWeight = units.reduce(
      (sum, unit) => sum + unit.num_units * (unit.commodities ?? []).reduce((s, c) => s + Number(c.weight_lbs), 0),
      0,
    )
    const totalWeight = unitsWeight > 0 ? String(unitsWeight) : wholePo ? (po.estimated_weight_lbs ?? null) : null
    const body: CreateBody =
      booker === 'us'
        ? {
            mode: 'manual_book',
            status: 'pending',
            booked_by: 'us',
            line_items: wholePo ? null : lineItems,
            ...pickupDefaults(po),
            total_weight_lbs: totalWeight,
            handling_units: units.length ? units : undefined,
          }
        : {
            mode: 'external',
            // Shipped once it has a ship date; otherwise pending, and the server marks it shipped
            // when a tracking or PRO number comes with it.
            status: shipDate ? 'shipped' : 'pending',
            booked_by: 'manufacturer',
            line_items: lineItems,
            ship_from_name: po.manufacturer?.name ?? null,
            carrier_name: carrier.trim() || null,
            tracking_numbers: tracking
              .split(/[,\n]/)
              .map((t) => t.trim())
              .filter(Boolean),
            pro_number: pro.trim() || null,
            actual_ship_date: shipDate || null,
          }
    if (mode) body.freight_mode = mode
    if (body.tracking_numbers?.length === 0) body.tracking_numbers = null
    create.mutate(body, {
      onSuccess: () => {
        toast.success(booker === 'us' ? 'Shipment added — check the details and book it' : 'Supplier shipment recorded')
        onDone()
      },
      onError: (error) => toast.error(formatApiErrorMessage('Shipment not added', error)),
    })
  }

  return (
    <Card className="py-4">
      <CardContent className="space-y-4">
        <form
          className="space-y-4"
          aria-label="Add shipment"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
          onKeyDown={(event) => event.key === 'Escape' && onDone()}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Who books</p>
              <ReviewChoiceGroup<Booker>
                label="Who books"
                value={booker}
                onChange={setBooker}
                options={[
                  { value: 'us', label: 'We book' },
                  { value: 'manufacturer', label: 'Supplier books' },
                ]}
              />
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Ships as</p>
              <ReviewChoiceGroup<Mode>
                label="Ships as"
                value={mode || null}
                onChange={setMode}
                options={[
                  { value: 'parcel', label: FREIGHT_MODE_LABEL.parcel },
                  { value: 'ltl', label: FREIGHT_MODE_LABEL.ltl },
                ]}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm">
                <span className="text-xs font-medium text-muted-foreground">Items </span>
                {wholePo ? `All items (${unitCount})` : `${unitCount} of ${items.reduce((s, i) => s + i.ordered, 0)} units`}
              </p>
              <Button type="button" variant="ghost" size="sm" onClick={() => setChoosingItems((v) => !v)} aria-expanded={choosingItems}>
                {choosingItems ? 'Done' : 'Change items'}
              </Button>
            </div>
            {choosingItems && (
              <ul className="divide-y rounded-md border">
                {items.map((item) => (
                  <li key={item.id} className="flex items-center gap-3 px-3 py-2">
                    <input
                      id={`add-item-${item.id}`}
                      type="checkbox"
                      className="size-4 accent-primary"
                      checked={item.included}
                      onChange={(event) =>
                        setItems(items.map((i) => (i.id === item.id ? { ...i, included: event.target.checked } : i)))
                      }
                    />
                    <Label htmlFor={`add-item-${item.id}`} className="min-w-0 flex-1 truncate text-sm font-medium">
                      {item.model}
                    </Label>
                    <InlineFieldInput
                      aria-label={`${item.model} quantity`}
                      inputMode="numeric"
                      className="w-16 text-right tabular-nums"
                      value={item.quantity}
                      disabled={!item.included}
                      onChange={(event) => {
                        const quantity = Math.max(0, Math.min(item.ordered, Number(event.target.value) || 0))
                        setItems(items.map((i) => (i.id === item.id ? { ...i, quantity } : i)))
                      }}
                    />
                    <span className="w-10 text-xs text-muted-foreground tabular-nums">of {item.ordered}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {booker === 'manufacturer' && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(
                [
                  ['Carrier', carrier, setCarrier, 'text'],
                  ['Tracking', tracking, setTracking, 'text'],
                  ['PRO', pro, setPro, 'text'],
                  ['Ship date', shipDate, setShipDate, 'date'],
                ] as const
              ).map(([label, value, set, type]) => (
                <div key={label} className="space-y-1">
                  <Label htmlFor={`add-${label}`} className="text-xs text-muted-foreground">
                    {label}
                  </Label>
                  {type === 'date' ? (
                    <Input id={`add-${label}`} type="date" className="h-8" value={value} onChange={(e) => set(e.target.value)} />
                  ) : (
                    <InlineFieldInput id={`add-${label}`} value={value} onChange={(e) => set(e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onDone} disabled={create.isPending}>
              Cancel
            </Button>
            <LoadingButton type="submit" loading={create.isPending} loadingText="Adding...">
              {booker === 'us' ? 'Add shipment' : 'Record shipment'}
            </LoadingButton>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
